import type { SceneTemplateVariation } from "@/features/lomdaEditorV2/lib/lomdaTypes";
import { SCENE_PADDING } from "../../sceneConstants";
import { CANVAS_W, CANVAS_H } from "../transform";
import type { ElementTransform, VerticalAlign } from "../types";
import type { LayoutSlots } from "./types";

// The A–E templates lay out inside the scene-container's padded box.
const GAP = 80; // template B column gap
const A_INLINE = 240; // template A extra horizontal padding

const pctX = (px: number) => (px / CANVAS_W) * 100;
const pctY = (px: number) => (px / CANVAS_H) * 100;

const contentX = pctX(SCENE_PADDING);
const contentY = pctY(SCENE_PADDING);
const contentW = pctX(CANVAS_W - 2 * SCENE_PADDING);
const contentH = pctY(CANVAS_H - 2 * SCENE_PADDING);
const gapW = pctX(GAP);

const box = (
    x: number,
    y: number,
    width: number,
    height: number
): ElementTransform => ({ x, y, width, height, rotation: 0 });

export const FULL_CONTENT = box(contentX, contentY, contentW, contentH);

// A: centered text, extra 240px inline padding, vertically centered band.
const A_X = pctX(SCENE_PADDING + A_INLINE);
const A_W = pctX(CANVAS_W - 2 * (SCENE_PADDING + A_INLINE));
export const CENTERED_TEXT = box(A_X, 25, A_W, 50);
const TITLE_TEXT = box(A_X, 33, A_W, 34);

/** Template B split — text column + media column for a media ratio. */
export function splitColumns(ratio: number): {
    text: ElementTransform;
    media: ElementTransform;
} {
    const r = Math.min(0.8, Math.max(0.2, ratio));
    const usableW = contentW - gapW;
    const textW = usableW * (1 - r);
    const mediaW = usableW * r;
    return {
        text: box(contentX, contentY, textW, contentH),
        media: box(contentX + textW + gapW, contentY, mediaW, contentH),
    };
}

// D: full-bleed media hero (320px) on top, text below.
const D_MEDIA_H = pctY(320);
const D_TEXT_Y = D_MEDIA_H + pctY(80);
const D_LAYOUT = {
    media: box(0, 0, 100, D_MEDIA_H),
    text: box(contentX, D_TEXT_Y, contentW, 100 - D_TEXT_Y - contentY),
};

export interface TemplateLayout {
    nameKey: string;
    slots: LayoutSlots;
    align: "center" | "start";
    /** Vertical placement of the text inside its slot. A is vertically centered. */
    textVerticalAlign: VerticalAlign;
    mediaFit: "contain" | "cover";
}

/**
 * The original A–E scene templates as free-canvas layouts. Single source of
 * truth shared by the on-load migration and the layouts sidebar, so a migrated
 * scene and "apply layout" place elements identically. Names describe the
 * actual rendered arrangement (the legacy `lomda.scene-template.*` strings are
 * stale and don't match what the templates draw).
 */
export const TEMPLATE_LAYOUTS: Record<SceneTemplateVariation, TemplateLayout> = {
    A: {
        nameKey: "lomda.layout.text-center",
        slots: { text: CENTERED_TEXT },
        align: "center",
        textVerticalAlign: "center",
        mediaFit: "cover",
    },
    B: {
        nameKey: "lomda.layout.split-left",
        slots: splitColumns(0.5),
        align: "start",
        textVerticalAlign: "top",
        mediaFit: "contain",
    },
    C: {
        nameKey: "lomda.layout.full-text",
        slots: { text: FULL_CONTENT },
        align: "start",
        textVerticalAlign: "top",
        mediaFit: "contain",
    },
    D: {
        nameKey: "lomda.layout.media-top",
        slots: D_LAYOUT,
        align: "start",
        textVerticalAlign: "top",
        mediaFit: "cover",
    },
    E: {
        nameKey: "lomda.layout.title",
        slots: { text: TITLE_TEXT },
        align: "center",
        textVerticalAlign: "top",
        mediaFit: "cover",
    },
};
